import _ from 'lodash';
import debounce from 'debounce';
import { mapState, mapActions } from 'vuex';
import Multiselect from 'vue-multiselect';
import { FILTER_TYPES } from '~/utils/constants';
import FormInput from '~/components//form-input/FormInput.vue';
import Daterangepicker from '~/components/daterangepicker/Daterangepicker.vue';
import Debug from '~/utils/debug';
import * as adapi from '~/utils/adapi';

const debug = Debug('Filters');
export default {
  props: {
    availableFields: Object,
    enabledFields: {
      type: Array,
      default: () => [],
    },
    filters: Object,
    enabledFilters: Array,
    availableFilters: Array,
  },
  components: {
    'form-input': FormInput,
    multiselect: Multiselect,
    daterangepicker: Daterangepicker,
  },
  data() {
    return {
      internalFilters: this.getFilterDefaultValues(),
      FILTER_TYPES,
      showCollapse: false,
      checkFilterValues: [
        'modes',
        'statuses',
        'type',
        'goalType',
        'clientLanguage',
        'statusFlows',
      ],
      typeaheadResults: {},
    };
  },
  computed: {
    ...mapState('searchFilters', ['data', 'visible']),
    internalAvailableFields() {
      // User must select at least one field
      const fields = {};
      const availableFields = _.cloneDeep(this.availableFields);
      _.forOwn(availableFields, (value, key) => {
        if ((this.enabledFields.length < 2 && this.enabledFields.indexOf(key) >= 0) || key === 'code') {
          value.disabled = true;
        } else {
          value.disabled = false;
        }
        fields[key] = value;
        fields[key].label = fields[key].translateColumn();
      });
      return fields;
    },
    internalEnabledFields: {
      get() {
        return this.enabledFields;
      },
      set(value) {
        this.$emit('update:enabledFields', value);
      },
    },
    filterGrid() {
      const rows = [];
      this.enabledFilters.forEach(filters => {
        const columns = [];
        filters.forEach(filterName => {
          const filter = this.availableFilters.find(f => f.slug === filterName);
          if (!filter) {
            debug.info('Tried to use unavailable filter', filterName);
            return;
          }

          filter.translateGroupLabel = filter.translateGroupLabel
            || (item => item[filter.options.groupLabel]);
          filter.translateItemLabel = filter.translateItemLabel
            || (item => item[filter.options.label]);
          filter.translateFilter = filter.translateFilter
              || (() => filter.name);

          if (filter) {
            const filterData = _.cloneDeep(_.get(this.data, filterName, []));
            filter.name = filter.translateFilter();
            filter.data = filterData.map(data => {
              if (filter.options.groupLabel !== null) {
                data[filter.options.groupLabel] = filter.translateGroupLabel(data);
              }

              if (filter.options.groupValues !== null) {
                data[filter.options.groupValues] = data[filter.options.groupValues]
                  .map(item => {
                    item[filter.options.label] = filter.translateItemLabel(item);
                    return item;
                  });
              } else {
                data[filter.options.label] = filter.translateItemLabel(data);
              }

              return data;
            });

            if (_.includes(this.checkFilterValues, filter.slug)) {
              if (filter.data.length > 1) {
                columns.push(filter);
              }
            } else {
              columns.push(filter);
            }
          }
        });
        if (columns.length > 0) {
          rows.push(columns);
        }
      });
      return rows;
    },
  },
  methods: {
    ...mapActions('searchFilters', ['setVisibility']),
    queryToObject() {
      this.internalFilters = this.getFilterDefaultValues();
      // Pass correctly filtered objects to our filters
      this.availableFilters.forEach(filter => {
        if (_.has(this.filters, filter.slug)) {
          const value = filter.toObject(this.filters[filter.slug], filter.data);
          if (value) {
            this.internalFilters[filter.slug] = value;
          }
        }
      });

      const text = _.get(this.filters, 'text', null);
      if (!_.isEmpty(text)) {
        this.internalFilters.text = text;
      }
    },
    onTypeahead(search, filter) {
      debug.info(search);
      this.$set(this.typeaheadResults, filter.slug, []);
      adapi.get(filter.requestPath, { search })
        .catch(() => [])
        .then(filter.formatItems || (i => i))
        .then(items => this.$set(this.typeaheadResults, filter.slug, items));
    },
    objectToFilters() {
      const filters = {};
      this.availableFilters.forEach(filter => {
        const value = _.get(this.internalFilters, filter.slug);
        if (!_.isEmpty(value)) {
          const result = filter.toFilter(value);
          if (!_.isEmpty(result)) {
            filters[filter.slug] = result;
          }
        }
      });

      const text = _.get(this.internalFilters, 'text', null);
      if (!_.isEmpty(text)) {
        filters.text = text;
      }
      return filters;
    },
    filter() {
      this.$emit('update:filters', this.objectToFilters(this.internalFilters));
      this.$emit('update', true);
    },
    clearAllFilters() {
      this.internalFilters = this.getFilterDefaultValues(false);
      this.filter();
    },
    getFilterDefaultValues(clearText = true) {
      const filters = {};
      this.availableFilters.forEach(f => {
        filters[f.slug] = f.default;
      });
      filters.text = clearText ? null : this.internalFilters.text;
      return filters;
    },
    exportData(filtered = false, type = 'xls') {
      this.$emit('export', { filtered, type });
    },
    clearFilterData(slug) {
      this.internalFilters[slug] = this.getFilterDefaultValue(slug);
    },
    isFilterClerable(slug) {
      const value = this.internalFilters[slug];
      return !_.isEmpty(value) && value !== this.getFilterDefaultValue(slug);
    },
    getFilterDefaultValue(slug) {
      return _.get(this.availableFilters.find(f => f.slug === slug), 'default') || null;
    },
    debounceInput: debounce(function debounceInputFilter() {
      this.filter();
    }, 500),
  },
  mounted() {
    debug.info('mounted');
    this.queryToObject();
    this.showCollapse = this.visible;
  },
  watch: {
    filters() {
      this.queryToObject();
    },
    showCollapse(value) {
      this.setVisibility(value);
    },
  },
};



// WEBPACK FOOTER //
// ./src/gotu-vue-common/src/components/search-table/filters/Filters.js